import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Clock, Calendar, Zap, CalendarPlus, MapPin, Trophy, Star, X, CreditCard, Users, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { PageHeader, FullPageLoader, EmptyState } from "@/components/common";
import { DEFAULT_TZ, tzLabel } from "@/lib/timezones";
import { useAuth } from "@/context/AuthContext";

const fmtHoldShort = (iso) => {
  if (!iso) return null;
  const totalMin = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
  if (totalMin < 60) return `${totalMin}m`;
  const d = Math.floor(totalMin / 1440), h = Math.floor((totalMin % 1440) / 60), m = totalMin % 60;
  return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
};

const statusStyle = (status) => {
  const map = {
    confirmed: { color: "#00ff9d", bg: "rgba(0,255,157,0.12)" },
    pending: { color: "#ffb020", bg: "rgba(255,176,32,0.12)" },
    active: { color: "#00e5ff", bg: "rgba(0,229,255,0.12)" },
    cancelled: { color: "#ff2e97", bg: "rgba(255,46,151,0.12)" },
    completed: { color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  };
  return map[status] || map.pending;
};

const isTournament = (b) => b.booking_type === "tournament" || b.booking_type === "tournament_participant";

const BookingCard = ({ b, stationMap, onSelect, reviewedIds, navigate }) => {
  const s = statusStyle(b.status);
  const names = (b.station_ids || []).map((id) => stationMap[id] || "Station").join(", ");
  const tournament = isTournament(b);
  const isPast = new Date(b.end_time).getTime() < Date.now();
  const eligibleForReview = isPast && (b.status === "confirmed" || b.status === "completed") && !reviewedIds.has(b.id);
  return (
    <button
      type="button"
      onClick={() => onSelect(b)}
      className="glass glass-hover rounded-2xl p-6 w-full text-left transition-transform hover:-translate-y-0.5"
      data-testid={`booking-${b.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            {tournament && (
              <span className="chip inline-flex items-center gap-1.5" style={{ background: "rgba(255,210,63,0.12)", border: "1px solid rgba(255,210,63,0.45)", color: "#ffd23f" }} data-testid={`booking-tournament-chip-${b.id}`}>
                <Trophy className="w-3.5 h-3.5" /> {b.tournament_name || "Tournament"}
              </span>
            )}
            <span className="chip capitalize" style={{ background: s.bg, border: `1px solid ${s.color}66`, color: s.color }}>{b.status}</span>
            {!tournament && <span className="font-display text-xl font-bold neon-cyan-text">${(b.total_amount || 0).toFixed(2)}</span>}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-slate-300 text-sm">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-500" />{new Date(b.start_time).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: DEFAULT_TZ })}</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" />{new Date(b.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })} – {new Date(b.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-500" />{names}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {b.points_earned > 0 && (
            <span className="chip" style={{ background: "rgba(255,176,32,0.12)", border: "1px solid rgba(255,176,32,0.4)", color: "#ffb020" }}>
              <Zap className="w-3.5 h-3.5" /> +{b.points_earned} pts
            </span>
          )}
          {eligibleForReview && (
            <span onClick={(e) => { e.stopPropagation(); navigate(`/review/${b.id}`); }} className="neon-btn px-4 py-2 text-xs cursor-pointer" data-testid={`leave-review-${b.id}`}>
              <Star className="w-3.5 h-3.5" /> Leave a review
            </span>
          )}
          {isPast && reviewedIds.has(b.id) && (
            <span onClick={(e) => { e.stopPropagation(); navigate(`/review/${b.id}`); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer" data-testid={`view-review-${b.id}`}>
              <Star className="w-3 h-3" fill="#ffb020" style={{ color: "#ffb020" }} /> View your review
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const formatDuration = (start, end) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const total = Math.round(ms / 60000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const BookingDetailsModal = ({ booking, stationMap, onClose, onPay, paying }) => {
  if (!booking) return null;
  const s = statusStyle(booking.status);
  const isPending = booking.status === "pending" && (booking.total_amount || 0) > 0;
  const expiresAt = booking.expires_at ? new Date(booking.expires_at) : null;
  const nowMs = Date.now();
  const holdLabel = (() => {
    if (!expiresAt) return null;
    const totalMin = Math.max(0, Math.round((expiresAt.getTime() - nowMs) / 60000));
    if (totalMin < 60) return `${totalMin} min`;
    const d = Math.floor(totalMin / 1440), h = Math.floor((totalMin % 1440) / 60), m = totalMin % 60;
    return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
  })();
  const tournament = isTournament(booking);
  const stationNames = (booking.station_ids || []).map((id) => stationMap[id] || "Station");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-slate-950 border border-white/10 text-white [&>button]:hidden">
        <div className="flex items-center justify-between mb-4">
          <DialogTitle className="font-display uppercase tracking-widest text-lg text-white">Booking details</DialogTitle>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-booking-details"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs text-slate-500 uppercase tracking-widest">#{(booking.id || "").slice(-8).toUpperCase()}</span>
          <span className="chip capitalize" style={{ background: s.bg, border: `1px solid ${s.color}66`, color: s.color }}>{booking.status}</span>
          {tournament && (
            <span className="chip inline-flex items-center gap-1.5" style={{ background: "rgba(255,210,63,0.12)", border: "1px solid rgba(255,210,63,0.45)", color: "#ffd23f" }}>
              <Trophy className="w-3.5 h-3.5" /> Tournament
            </span>
          )}
        </div>

        {isPending && holdLabel !== null && (
          <div className="rounded-xl p-3 mb-5 flex items-start gap-3" style={{ background: "rgba(255,176,32,0.10)", border: "1px solid rgba(255,176,32,0.4)" }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: "#ffb020" }} />
            <div className="text-sm">
              <p className="text-slate-100 font-semibold">Slot is being held</p>
              <p className="text-slate-400">Pay within <span className="font-bold" style={{ color: "#ffb020" }}>{holdLabel}</span> or we&apos;ll release it.</p>
            </div>
          </div>
        )}

        <div className="space-y-3 text-sm">
          <Row icon={<Calendar className="w-4 h-4 text-slate-500" />} label="Date" value={new Date(booking.start_time).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: DEFAULT_TZ })} />
          <Row icon={<Clock className="w-4 h-4 text-slate-500" />} label="Time" value={`${new Date(booking.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })} – ${new Date(booking.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })} · ${formatDuration(booking.start_time, booking.end_time)}`} />
          <Row icon={<MapPin className="w-4 h-4 text-slate-500" />} label={stationNames.length > 1 ? `${stationNames.length} Stations` : "Station"} value={stationNames.join(", ") || "—"} />
          <Row icon={<Users className="w-4 h-4 text-slate-500" />} label="Party size" value={`${booking.party_size} player${booking.party_size === 1 ? "" : "s"}`} />
          {booking.motion_selected && <Row icon={<Zap className="w-4 h-4" style={{ color: "#ff2e97" }} />} label="Motion add-on" value="Yes" />}
          {booking.special_requests && <Row icon={<Ticket className="w-4 h-4 text-slate-500" />} label="Notes" value={booking.special_requests} />}
        </div>

        <div className="border-t border-white/10 mt-5 pt-5 space-y-2 text-sm">
          {booking.tax_amount > 0 && <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>${(booking.subtotal_amount ?? (booking.total_amount - booking.tax_amount)).toFixed(2)}</span></div>}
          {booking.tax_amount > 0 && <div className="flex justify-between text-slate-500"><span>HST ({booking.tax_rate || 14}%)</span><span>+${(booking.tax_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-slate-300"><span>Total</span><span className="font-display font-bold text-lg neon-cyan-text">${(booking.total_amount || 0).toFixed(2)}</span></div>
          {booking.points_used > 0 && <div className="flex justify-between text-slate-500"><span>Points used</span><span style={{ color: "#ffb020" }}>-{booking.points_used}</span></div>}
          {booking.points_earned > 0 && <div className="flex justify-between text-slate-500"><span>Points earned</span><span style={{ color: "#00ff9d" }}>+{booking.points_earned}</span></div>}
        </div>

        {isPending && (
          <button
            onClick={() => onPay(booking)}
            disabled={paying}
            className="neon-btn w-full py-3 mt-6 disabled:opacity-50"
            data-testid="pay-booking-btn"
          >
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {paying ? "Redirecting…" : "Pay now"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white break-words">{value}</p>
    </div>
  </div>
);

export default function MyBookings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stationMap, setStationMap] = useState({});
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [paying, setPaying] = useState(false);
  const [busyGid, setBusyGid] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get("/bookings/my"),
      apiClient.get("/stations"),
      apiClient.get("/reviews/mine"),
      apiClient.get("/group-bookings/mine"),
    ])
      .then(([b, s, r, g]) => {
        setBookings(b.data);
        const map = {};
        s.data.forEach((st) => { map[st.id] = st.name; });
        setStationMap(map);
        setReviewedIds(new Set((r.data || []).map((rv) => rv.booking_id)));
        setGroups(g.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const payShare = async (gid) => {
    setBusyGid(gid);
    try { const { data } = await apiClient.post(`/group-bookings/${gid}/pay?client_origin=${encodeURIComponent(window.location.origin)}`); window.location.href = data.url; }
    catch (err) { toast.error(getErrorMessage(err, "Payment failed")); setBusyGid(null); }
  };
  const coverBalance = async (gid) => {
    setBusyGid(gid);
    try { const { data } = await apiClient.post(`/group-bookings/${gid}/cover-balance?client_origin=${encodeURIComponent(window.location.origin)}`); window.location.href = data.url; }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); setBusyGid(null); }
  };

  const payNow = async (b) => {
    setPaying(true);
    try {
      const { data } = await apiClient.post(`/payments/checkout?booking_id=${b.id}&client_origin=${encodeURIComponent(window.location.origin)}`);
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.success("Booking confirmed");
        setSelected(null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not start payment"));
    } finally { setPaying(false); }
  };

  if (loading) return <FullPageLoader label="Loading Bookings" />;

  const standard = bookings.filter((b) => !isTournament(b));
  const tournaments = bookings.filter((b) => isTournament(b));

  return (
    <div>
      <PageHeader
        title="My Bookings"
        subtitle="Your upcoming and past gaming sessions."
        right={
          <button onClick={() => navigate("/booking")} className="neon-btn px-6 py-3" data-testid="new-booking-btn">
            <CalendarPlus className="w-4 h-4" /> New Booking
          </button>
        }
      />

      <p className="text-slate-500 text-xs mb-6 flex items-center gap-1.5" data-testid="atlantic-time-note">
        <Clock className="w-3.5 h-3.5" /> All times shown in {tzLabel(DEFAULT_TZ)} — the lounge&apos;s local time.
      </p>

      {(() => {
        const actionable = groups.filter((g) => {
          if (g.status === "confirmed" || g.status === "cancelled") return false;
          const mine = g.participants.find((p) => p.user_id === user?.id);
          const isOrg = g.organizer_id === user?.id;
          const iCanPay = mine && mine.status === "accepted" && !mine.paid && (g.pay_mode === "split" || isOrg);
          const iCanCover = isOrg && g.status === "collecting_payment" && g.participants.some((p) => p.status === "accepted" && !p.paid && p.user_id !== user?.id);
          return iCanPay || iCanCover;
        });
        if (actionable.length === 0) return null;
        return (
          <section className="mb-8" data-testid="group-invites-section">
            <h2 className="font-display text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color: "#8b5cf6" }} /> Group Sessions — Payment Needed
              <span className="text-slate-600">· {actionable.length}</span>
            </h2>
            <div className="grid gap-4">
              {actionable.map((g) => {
                const mine = g.participants.find((p) => p.user_id === user?.id);
                const isOrg = g.organizer_id === user?.id;
                const unpaid = g.participants.filter((p) => p.status === "accepted" && !p.paid);
                const coverAmt = unpaid.reduce((s, p) => s + (Number(p.share) || 0), 0);
                const when = g.start_time ? new Date(g.start_time).toLocaleString(undefined, { timeZone: DEFAULT_TZ }) : "TBD";
                const hold = fmtHoldShort(g.expires_at);
                return (
                  <div key={g.id} className="rounded-xl p-4" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.3)" }} data-testid={`mybookings-group-${g.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-white font-semibold text-sm">{when}</p>
                        <p className="text-xs text-slate-400">{g.pay_mode === "split" ? "Split evenly" : "Organizer pays"} · ${g.total_amount}{hold ? ` · Hold expires in ${hold}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {mine && mine.status === "accepted" && !mine.paid && (g.pay_mode === "split" || isOrg) && (
                          <button onClick={() => payShare(g.id)} disabled={busyGid === g.id} className="neon-btn px-4 py-2 text-sm disabled:opacity-60" data-testid={`mybookings-group-pay-${g.id}`}>
                            {busyGid === g.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Pay $${mine.share}`}
                          </button>
                        )}
                        {isOrg && g.status === "collecting_payment" && unpaid.some((p) => p.user_id !== user?.id) && (
                          <button onClick={() => coverBalance(g.id)} disabled={busyGid === g.id} className="neon-outline-btn px-4 py-2 text-sm disabled:opacity-60" data-testid={`mybookings-group-cover-${g.id}`}>
                            Cover balance (${coverAmt.toFixed(2)})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {bookings.length === 0 ? (
        <EmptyState
          icon={Ticket}
          title="No bookings yet"
          subtitle="Reserve a premium gaming station and it will appear here."
          action={<button onClick={() => navigate("/booking")} className="neon-btn px-6 py-3"><CalendarPlus className="w-4 h-4" /> Book a Station</button>}
        />
      ) : (
        <div className="space-y-8">
          <section data-testid="standard-bookings-section">
            <h2 className="font-display text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
              <Ticket className="w-4 h-4" style={{ color: "#00e5ff" }} /> Standard Bookings
              <span className="text-slate-600">· {standard.length}</span>
            </h2>
            {standard.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No standard bookings yet.</p>
            ) : (
              <div className="grid gap-4">
                {standard.map((b) => <BookingCard key={b.id} b={b} stationMap={stationMap} onSelect={setSelected} reviewedIds={reviewedIds} navigate={navigate} />)}
              </div>
            )}
          </section>

          <section data-testid="tournament-bookings-section">
            <h2 className="font-display text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4" style={{ color: "#ffd23f" }} /> Tournament Bookings
              <span className="text-slate-600">· {tournaments.length}</span>
            </h2>
            {tournaments.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">You haven&apos;t registered for any tournaments yet.</p>
            ) : (
              <div className="grid gap-4">
                {tournaments.map((b) => <BookingCard key={b.id} b={b} stationMap={stationMap} onSelect={setSelected} reviewedIds={reviewedIds} navigate={navigate} />)}
              </div>
            )}
          </section>
        </div>
      )}

      <BookingDetailsModal booking={selected} stationMap={stationMap} onClose={() => setSelected(null)} onPay={payNow} paying={paying} />
    </div>
  );
}
